import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from "react";
import { Alert, Snackbar, AlertColor } from "@mui/material";
import { useTranslation } from "react-i18next";

interface Notification {
  id?: string | number;
  message: string;
  title?: string;
  severity: AlertColor;
  duration?: number;
  actionLabel?: string;
  onAction?: () => void;
}

interface NotificationContextType {
  showNotification: (
    message: string,
    severity?: AlertColor,
    duration?: number,
  ) => void;
  showSuccess: (message: string, duration?: number) => void;
  showError: (message: string, duration?: number) => void;
  showWarning: (message: string, duration?: number) => void;
  showInfo: (message: string, duration?: number) => void;
  // Order-specific notifications
  showOrderAvailable: (orderData: {
    id: number;
    title: string;
    address: string;
    price: number;
  }) => void;
  showOrderStatusChanged: (
    orderId: number,
    orderTitle: string,
    newStatus: string,
    statusRu?: string,
  ) => void;
  showOrderAssigned: (orderId: number, orderTitle: string) => void;
  showOrderCancelled: (
    orderId: number,
    orderTitle: string,
    reason?: string,
  ) => void;
  showOrderCompleted: (orderId: number, orderTitle: string) => void;
  showPerformerResponded: (
    orderId: number,
    performerName: string,
    orderTitle: string,
    performerRating?: number,
    performerCompletedOrders?: number,
  ) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined,
);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotification must be used within a NotificationProvider",
    );
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
}) => {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [notification, setNotification] = useState<Notification>({
    message: "",
    severity: "success",
    duration: 6000,
  });

  const showNotification = useCallback(
    (
      message: string,
      severity: AlertColor = "success",
      duration: number = 6000,
    ) => {
      setNotification({ message, severity, duration });
      setOpen(true);
    },
    [],
  );

  const showSuccess = useCallback(
    (message: string, duration?: number) => {
      showNotification(message, "success", duration);
    },
    [showNotification],
  );

  const showError = useCallback(
    (message: string, duration?: number) => {
      showNotification(message, "error", duration);
    },
    [showNotification],
  );

  const showWarning = useCallback(
    (message: string, duration?: number) => {
      showNotification(message, "warning", duration);
    },
    [showNotification],
  );

  const showInfo = useCallback(
    (message: string, duration?: number) => {
      showNotification(message, "info", duration);
    },
    [showNotification],
  );

  // Order-specific notifications
  const showOrderAvailable = useCallback(
    (orderData: {
      id: number;
      title: string;
      address: string;
      price: number;
    }) => {
      setNotification({
        id: `order-${orderData.id}`,
        title: t("notifNewOrder"),
        message: `${orderData.title}: ${orderData.address} - ${orderData.price} ${t("currency")}`,
        severity: "info",
        duration: 8000,
        actionLabel: t("view"),
        onAction: () => {
          window.location.href = `/orders/${orderData.id}`;
        },
      });
      setOpen(true);
    },
    [],
  );

  const showOrderStatusChanged = useCallback(
    (
      orderId: number,
      orderTitle: string,
      newStatus: string,
      statusRu?: string,
    ) => {
      const statusLabels: Record<string, string> = {
        pending: t("status_pending"),
        assigned: t("status_assigned"),
        in_progress: t("status_in_progress"),
        completed: t("status_completed"),
        confirmed: t("status_confirmed"),
        cancelled: t("status_cancelled"),
      };

      const displayStatus =
        statusRu && !statusLabels[statusRu]
          ? statusRu
          : statusLabels[newStatus] || statusRu || t("status_unknown");

      setNotification({
        id: `status-${orderId}`,
        title: t("notifStatusChanged"),
        message: `${orderTitle}: ${displayStatus}`,
        severity: "info",
        duration: 6000,
        actionLabel: t("view"),
        onAction: () => {
          window.location.href = `/orders/${orderId}`;
        },
      });
      setOpen(true);
    },
    [],
  );

  const showOrderAssigned = useCallback(
    (orderId: number, orderTitle: string) => {
      setNotification({
        id: `assigned-${orderId}`,
        title: t("notifOrderAssigned"),
        message: t("notifYouAssigned", { orderTitle }),
        severity: "success",
        duration: 8000,
        actionLabel: t("goToOrder"),
        onAction: () => {
          window.location.href = `/orders/${orderId}`;
        },
      });
      setOpen(true);
    },
    [],
  );

  const showOrderCancelled = useCallback(
    (orderId: number, orderTitle: string, reason?: string) => {
      setNotification({
        id: `cancelled-${orderId}`,
        title: t("notifOrderCancelled"),
        message: `${orderTitle}${reason ? `: ${reason}` : ""}`,
        severity: "error",
        duration: 8000,
        actionLabel: t("view"),
        onAction: () => {
          window.location.href = `/orders/${orderId}`;
        },
      });
      setOpen(true);
    },
    [],
  );

  const showOrderCompleted = useCallback(
    (orderId: number, orderTitle: string) => {
      setNotification({
        id: `completed-${orderId}`,
        title: t("notifOrderCompleted"),
        message: t("notifOrderCompletedMsg", { orderTitle }),
        severity: "success",
        duration: 6000,
        actionLabel: t("view"),
        onAction: () => {
          window.location.href = `/orders/${orderId}`;
        },
      });
      setOpen(true);
    },
    [],
  );

  const showPerformerResponded = useCallback(
    (
      orderId: number,
      performerName: string,
      orderTitle: string,
      performerRating?: number,
      performerCompletedOrders?: number,
    ) => {
      const stars = performerRating
        ? "⭐".repeat(Math.round(performerRating))
        : "";
      const experience = performerCompletedOrders
        ? ` (${performerCompletedOrders} ${t("ordersAbbr")})`
        : "";

      setNotification({
        id: `responded-${orderId}`,
        title: t("notifNewResponse"),
        message: t("notifNewResponseMsg", { performerName, stars, experience, orderTitle }),
        severity: "info",
        duration: 10000,
        actionLabel: t("selectPerformer"),
        onAction: () => {
          window.location.href = `/orders/${orderId}`;
        },
      });
      setOpen(true);
    },
    [],
  );

  const handleClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setOpen(false);
  };

  const handleAction = () => {
    if (notification.onAction) {
      notification.onAction();
    }
    setOpen(false);
  };

  return (
    <NotificationContext.Provider
      value={{
        showNotification,
        showSuccess,
        showError,
        showWarning,
        showInfo,
        showOrderAvailable,
        showOrderStatusChanged,
        showOrderAssigned,
        showOrderCancelled,
        showOrderCompleted,
        showPerformerResponded,
      }}
    >
      {children}
      <Snackbar
        open={open}
        autoHideDuration={notification.duration}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleClose}
          severity={notification.severity}
          variant="filled"
          sx={{ width: "100%" }}
          action={
            notification.actionLabel ? (
              <button
                onClick={handleAction}
                style={{
                  color: "inherit",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                {notification.actionLabel}
              </button>
            ) : undefined
          }
        >
          {notification.title && <strong>{notification.title}</strong>}
          {notification.title && notification.message && <br />}
          {notification.message}
        </Alert>
      </Snackbar>
    </NotificationContext.Provider>
  );
};
